import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SlidersHorizontal, History, Lock } from 'lucide-react';
import { toast } from 'sonner';

const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const FIELDS = ['stage_2_min', 'stage_3_min', 'stage_4_min'];
const LABELS = {
  stage_2_min: 'Stabilization starts at',
  stage_3_min: 'Deepening starts at',
  stage_4_min: 'Embodiment starts at',
};

const ThresholdInputs = ({ values, onChange, testPrefix }) => (
  <div className="grid sm:grid-cols-3 gap-3">
    {FIELDS.map((field) => (
      <div key={field}>
        <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1.5">
          {LABELS[field]}
        </label>
        <input
          data-testid={`${testPrefix}-${field}`}
          type="number"
          min="1"
          max="99"
          value={values[field] ?? ''}
          onChange={(e) => onChange({ ...values, [field]: e.target.value })}
          className="w-full bg-gray-800 border border-gray-600 rounded-md px-3 py-1.5 text-white
                     focus:outline-none focus:border-cyan-400 transition-colors"
        />
      </div>
    ))}
  </div>
);

export const ThresholdsCard = () => {
  const [data, setData] = useState(null);
  const [defaults, setDefaults] = useState({});
  const [overrides, setOverrides] = useState({});
  const [openLocation, setOpenLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/assessments/thresholds`, { headers: headers() });
      setData(res.data);
      setDefaults(res.data.current.default_values);
      setOverrides(res.data.current.overrides || {});
    } catch {
      toast.error('Could not load thresholds');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const dirty =
    FIELDS.some((f) => String(defaults[f]) !== String(data.current.default_values[f])) ||
    JSON.stringify(overrides) !== JSON.stringify(data.current.overrides || {});

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        default_values: FIELDS.reduce((acc, f) => ({ ...acc, [f]: Number(defaults[f]) }), {}),
        overrides: Object.entries(overrides).reduce((acc, [loc, vals]) => ({
          ...acc,
          [loc]: FIELDS.reduce((v, f) => ({ ...v, [f]: Number(vals[f]) }), {}),
        }), {}),
      };
      const res = await axios.put(`${API}/admin/assessments/thresholds`, payload, { headers: headers() });
      setData((prev) => ({ ...prev, current: res.data.current, history: res.data.history }));
      setDefaults(res.data.current.default_values);
      setOverrides(res.data.current.overrides || {});
      toast.success(`Saved as version ${res.data.current.version}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save thresholds');
    } finally {
      setSaving(false);
    }
  };

  const toggleOverride = (locationId) => {
    setOverrides((prev) => {
      const next = { ...prev };
      if (next[locationId]) delete next[locationId];
      else next[locationId] = { ...defaults };
      return next;
    });
  };

  return (
    <Card className="bg-gray-900/60 border-gray-700" data-testid="thresholds-card">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <SlidersHorizontal className="w-5 h-5 text-cyan-400" />
              Stage Thresholds
            </h3>
            <p className="text-sm text-gray-400 mt-1 max-w-2xl">
              Where each developmental stage begins on the 0–100 score. The default applies to
              every assessment; override a single location only where it needs to differ.
            </p>
          </div>
          <Badge variant="outline" className="text-gray-300 border-gray-600 whitespace-nowrap"
                 data-testid="thresholds-version">
            Version {data.current.version}
          </Badge>
        </div>

        <div className="mt-5 flex items-center gap-4 text-sm">
          <span className="flex items-center gap-1.5 text-gray-500">
            <Lock className="w-3.5 h-3.5" /> Awakening starts at {data.stage_1_min}
          </span>
          <span className="flex items-center gap-1.5 text-gray-500">
            <Lock className="w-3.5 h-3.5" /> Top of scale {data.stage_4_max}
          </span>
        </div>

        <div className="mt-4">
          <h4 className="text-sm font-semibold text-gray-200 mb-2">Program default</h4>
          <ThresholdInputs values={defaults} onChange={setDefaults} testPrefix="threshold-default" />
        </div>

        <div className="mt-6">
          <h4 className="text-sm font-semibold text-gray-200 mb-2">
            Per-location overrides
          </h4>
          <div className="space-y-1.5">
            {data.locations.map((loc) => {
              const active = Boolean(overrides[loc.id]);
              const open = openLocation === loc.id;
              return (
                <div key={loc.id} className="bg-gray-800/50 border border-gray-700 rounded-md">
                  <button
                    onClick={() => setOpenLocation(open ? null : loc.id)}
                    data-testid={`threshold-location-${loc.id}`}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 text-left
                               hover:bg-gray-800 transition-colors rounded-md"
                  >
                    <span className="text-sm text-gray-200">{loc.label}</span>
                    <span className={`text-xs whitespace-nowrap ${
                      active ? 'text-amber-300' : 'text-gray-500'
                    }`}>
                      {active ? 'Override active' : 'Using default threshold set'}
                    </span>
                  </button>
                  {open && (
                    <div className="px-3 pb-3 pt-1 border-t border-gray-700 space-y-3">
                      <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={active}
                          onChange={() => toggleOverride(loc.id)}
                          data-testid={`threshold-override-toggle-${loc.id}`}
                          className="accent-cyan-500"
                        />
                        Override the default for this assessment
                      </label>
                      {active && (
                        <ThresholdInputs
                          values={overrides[loc.id]}
                          onChange={(vals) => setOverrides({ ...overrides, [loc.id]: vals })}
                          testPrefix={`threshold-override-${loc.id}`}
                        />
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-5 flex items-center gap-3 flex-wrap">
          <Button
            onClick={save}
            disabled={!dirty || saving}
            data-testid="thresholds-save-btn"
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
          >
            {saving ? 'Saving…' : 'Save new version'}
          </Button>
          {dirty && (
            <button
              onClick={() => {
                setDefaults(data.current.default_values);
                setOverrides(data.current.overrides || {});
              }}
              data-testid="thresholds-reset-btn"
              className="text-sm text-gray-400 hover:text-gray-200 transition-colors"
            >
              Discard changes
            </button>
          )}
          <button
            onClick={() => setShowHistory((v) => !v)}
            data-testid="thresholds-history-toggle"
            className="ml-auto text-sm text-gray-400 hover:text-gray-200 flex items-center gap-1.5
                       transition-colors"
          >
            <History className="w-4 h-4" />
            {showHistory ? 'Hide' : 'Show'} history ({data.history.length})
          </button>
        </div>

        <p className="text-xs text-gray-500 mt-3">
          Changing a threshold never moves a past member's stage. Each attempt stores the
          threshold set it was scored against.
        </p>

        {showHistory && (
          <div className="mt-4 border-t border-gray-700 pt-4 space-y-2" data-testid="thresholds-history">
            {data.history.map((set, i) => {
              const previous = data.history[i + 1];
              const changed = previous && FIELDS.some(
                (f) => set.default_values[f] !== previous.default_values[f],
              );
              return (
                <div key={set.id} className="text-xs text-gray-400 flex flex-wrap gap-x-3 gap-y-1">
                  <span className="text-gray-300 font-medium w-12">v{set.version}</span>
                  <span className="w-44">{new Date(set.created_at).toLocaleString()}</span>
                  <span className="w-52 truncate">{set.created_by_email || '—'}</span>
                  <span className="w-32">
                    0 / {FIELDS.map((f) => set.default_values[f]).join(' / ')} / 100
                  </span>
                  <span className="w-28">
                    {Object.keys(set.overrides || {}).length} override
                    {Object.keys(set.overrides || {}).length === 1 ? '' : 's'}
                  </span>
                  {changed && (
                    <span className="text-amber-300">
                      was 0 / {FIELDS.map((f) => previous.default_values[f]).join(' / ')} / 100
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ThresholdsCard;
